// Lightweight i18n: catalan default, castellano, english
import { createContext, useContext, useEffect, useState } from "react";

const DICT = {
  ca: {
    nav: {
      sala: "La Sala",
      eventos: "Esdeveniments",
      disponibilidad: "Disponibilitat",
      configuraciones: "Experiències",
      galeria: "Galeria",
      contacto: "Contacte",
      ubicacion: "Ubicació",
      admin: "Admin",
      reserveCTA: "Reserva la teva data",
      tarifas: "Tarifes",
      gastronomia: "Gastronomia",
    },
    cta: {
      seeCalendar: "Veure disponibilitat →",
      bookDate: "Reserva la teva data →",
      pickDate: "Tria la teva data →",
      yourDate: "Imagina-ho a Aura · tria data →",
      customExperience: "Crea la teva experiència customitzada al 100% des de 0 →",
      bookOwn: "Crea el teu propi esdeveniment →",
    },
    gastronomy: {
      eyebrow: "Gastronomia",
      title: "Ens adaptem al teu paladar",
      desc: "Fem esdeveniments gastronòmics de tot tipus amb xef privat. Tu tries la temàtica, el pressupost i el tipus de cuina — nosaltres dissenyem el menú a mida.",
    },
    tariffs: {
      eyebrow: "Tarifes",
      titleA: "Preus i tarifes",
      titleHl: "lloguer",
      titleB: "d'espai per a esdeveniments",
      desc: "Aura està disponible tots els dies de la setmana. Tria la franja que millor s'adapti al teu esdeveniment.",
      capacity: "Aforament: fins a 70 persones",
      standardTitle: "Aura està disponible tots els dies de la setmana",
      specialsHl: "Els nostres",
      specialsTitle: "especials",
      cta: "Reserva la teva data →",
      footnote: "Tarifes informatives · IVA no inclòs",
      colDay: "Dia",
      colSchedule: "Horari",
      colShift: "Torn",
      colAmount: "Import",
      reserveBtn: "Reservar",
      reserveBtnLong: "Reservar aquesta franja",
    },
    hero: {
      eyebrow: "Espai d'esdeveniments · Sant Cugat del Vallès",
      titleA: "",
      titleHl: "",
      titleB: "",
      desc:
        "Aura és un espai d'esdeveniments boutique, flexible i amb personalitat pròpia, pensat perquè marques, empreses i particulars puguin donar forma a experiències memorables en un entorn cuidat, contemporani i adaptable.",
      ctaCalendar: "Veure disponibilitat pel teu esdeveniment",
      ctaEvents: "Entrades pròxims esdeveniments",
    },
    venue: {
      eyebrow: "Un espai amb molta Aura",
      title: "Un espai. Múltiples maneres de viure'l",
      desc:
        "Aura dona resposta a una nova manera d'entendre els esdeveniments. Un espai versàtil i amb personalitat pròpia que combina disseny, calidesa i flexibilitat per acollir experiències molt diverses sense perdre identitat.",
      capacityTitle: "Aforament màxim",
      capacityValue: "Fins a 70 persones",
      areaTitle: "Espai",
      areaValue: "100 m² + terrassa",
      equipTitle: "Equipament inclòs",
      equipList: [
        "Il·luminació regulable",
        "Climatització i accessibilitat",
        "Zona de còctels i barra",
        "Terrassa exterior",
        "So professional i micròfons sense fils (opcionals)",
        "Projector HD i pantalla (opcionals)",
      ],
      audienceEyebrow: "Per a qui és Aura?",
      audienceTitle: "Un espai que s'adapta a tu",
      audiences: [
        { name: "Empreses i marques", desc: "Llançaments de producte, presentacions, sopars d'equip, jornades i formacions." },
        { name: "Professionals", desc: "Showrooms, networking, workshops i tallers per a clients o comunitats." },
        { name: "Particulars", desc: "Aniversaris, batejos, comiats, sopars privats i celebracions amb chef privat." },
        { name: "Comunitats", desc: "Trobades, festes temàtiques i esdeveniments culturals amb personalitat." },
      ],
      dnaEyebrow: "L'ADN d'Aura",
      dnaTitle: "Un espai camaleònic amb Aura pròpia",
      dnaValues: [
        { name: "Camaleònic", desc: "Cada esdeveniment és únic. La sala es transforma per acompanyar-lo." },
        { name: "Social", desc: "Pensat per compartir, connectar, celebrar i gaudir en companyia." },
        { name: "Cuidat", desc: "Disseny, llum i detalls pensats perquè tot encaixi sense esforç." },
        { name: "Atrevit", desc: "Contemporani, viu i amb caràcter. Sense por de marcar la diferència." },
      ],
    },
    events: {
      eyebrow: "L'agenda d'Aura",
      title: "Esdeveniments propis",
      desc:
        "Vetllades obertes, cates, concerts íntims i festes temàtiques. Tria la teva i guarda't la plaça abans que volin.",
      empty:
        "Aviat publicarem nous esdeveniments. Mentrestant, pots reservar la teva data privada al calendari.",
      seeDetail: "Veure detall →",
      ticket: "Entrada",
      publicTag: "Públic",
      privateTag: "Privat",
      ctaBanner: "Vols la teva pròpia data? Reserva una tarda, nit o cap de setmana — la sala és teva.",
    },
    calendar: {
      eyebrow: "Disponibilitat",
      title: "Tria la teva data",
      pitchTitle: "Convertim qualsevol dia en un dia especial",
      pitchDesc:
        "Tria una data lliure i te la reservem en pocs minuts. Cates de vins, sopars d'empresa, batejos, chef privat, aniversaris rodons o un dimecres qualsevol que vols recordar. A Aura tu poses el motiu — nosaltres posem l'escenari.",
      legendFree: "Lliure",
      legendPrivate: "Privat",
      legendPublic: "Esdeveniment públic",
      tooltipPublic: (t) => t,
      tooltipPrivate: "Data reservada",
      tooltipFree: "Data lliure · clic per reservar",
    },
    layouts: {
      eyebrow: "Configuracions de sala",
      title: "Una sala, mil experiències",
      desc:
        "La mateixa sala canvia per a cada ocasió. Alguns exemples han estat:",
      catering: {
        eyebrow: "Càtering a mida",
        title: "Tu tries la temàtica · nosaltres cuinem",
        desc:
          "Inclòs amb la sala: dissenyem un menú a mida amb tu — tapes mediterrànies, sopar de gala, brunch dolç o cuina internacional. Et conjuntem decoració, càtering i ambient amb la teva temàtica.",
      },
      items: [
        { name: "Celebracions", desc: "Aniversaris, batejos, comunions, posades de llarg i graduacions. Pensades al detall." },
        { name: "Esdeveniments d'empresa", desc: "Dinars i sopars d'empresa, reunions, actes corporatius i presentacions." },
        { name: "Pop-ups", desc: "Marca convidada, llançaments, showrooms i activacions efímeres amb identitat." },
      ],
    },
    gallery: {
      eyebrow: "Inspiració",
      title: "Una nova manera de trobar-se",
    },
    offsite: {
      eyebrow: "AURA Esdeveniments Externs",
      title: "Vols que portem l'organització d'Aura fora del nostre local?",
      desc: "Si tens un espai propi, una terrassa, una masia, una oficina… nosaltres hi portem tota l'experiència Aura. T'ajudem a dissenyar l'esdeveniment i ens encarreguem de totes les peces perquè només hagis de gaudir.",
      items: [
        "Event management",
        "Idees i conceptualització",
        "Disseny d'experiència",
        "Decoració",
        "Catering",
        "Barra i còctels",
        "Animació",
        "DJ i so",
        "I molt més…",
      ],
    },
    contact: {
      eyebrow: "Parlem",
      title1: "Explica'ns com",
      titleHl: "somies",
      title2: "el teu esdeveniment.",
      desc:
        "Respon el formulari i en enviar t'obrirem WhatsApp amb un missatge llest. Coordinarem amb tu en minuts.",
      address: "Adreça",
      phone: "Telèfon",
      labelName: "Nom",
      labelEmail: "Email",
      labelPhone: "Telèfon",
      labelMessage: "Missatge",
      placeholderMessage: "Explica'ns sobre el teu esdeveniment ideal…",
      submit: "Enviar i obrir WhatsApp",
      submitting: "Enviant…",
      required: "Si us plau completa els camps obligatoris.",
      sent: "Missatge enviat. Continuem per WhatsApp.",
    },
    map: {
      eyebrow: "Com arribar",
      title: "Rambla del Celler, 93",
      desc:
        "08172 Sant Cugat del Vallès, Barcelona.",
      fgcLabel: "FGC",
      fgcValue: "Sant Cugat — 5 min",
      parkingLabel: "Pàrquing",
      parkingValue: "Plaça Lluís Millet",
      cta: "Obrir a Maps →",
    },
    footer: {
      nav: "Navegació",
      contact: "Contacte",
      rights: "© {year} Aura Events",
      adminLink: "Accés admin",
    },
    reserve: {
      eyebrow: "Reserva privada",
      desc:
        "Deixa'ns les teves dades i t'enviem a WhatsApp amb un missatge llest perquè confirmem amb tu.",
      placeholderNotes: "Tipus d'esdeveniment, nombre aproximat de persones…",
      notesLabel: "Notes (opcional)",
      submit: "Enviar i obrir WhatsApp",
      messageTemplate: (n, d, e, p, notes) =>
        `Hola Aura, sóc ${n}. Vull reservar el ${d} per a un esdeveniment privat. Email: ${e} · Tel: ${p}. ${notes ? `Notes: ${notes}` : ""}`,
      tariffEyebrow: "Reserva de franja",
      tariffTitle: "Reservar franja",
      datePrefLabel: "Data preferent",
      datePrefPlaceholder: "Ex: 15 de març de 2026",
      tariffMessage: (n, e, p, tariff, prefDate, notes) =>
        `Hola Aura, sóc ${n}. Vull reservar la franja "${tariff.name}" (${tariff.days} · ${tariff.schedule}) — ${tariff.price}€ + IVA. ${prefDate ? `Data preferent: ${prefDate}. ` : ""}Email: ${e} · Tel: ${p}. ${notes ? `Notes: ${notes}` : ""}`,
    },
    detail: {
      back: "Tornar",
      tickets: "Compra d'entrades",
      reserve: "Reserva la teva plaça",
      qty: "Entrades",
      total: "Total",
      buy: "Comprar entrada",
      buying: "Processant…",
      footnote: "Et redirigim a WhatsApp per confirmar el pagament.",
      type: "Tipus",
      typeValue: "Esdeveniment públic",
      place: "Lloc",
      placeValue: "Aura Events, Sant Cugat",
      ticket: "Entrada",
    },
  },
  es: {
    nav: { sala: "La Sala", eventos: "Eventos", disponibilidad: "Disponibilidad", configuraciones: "Experiencias", galeria: "Galería", contacto: "Contacto", ubicacion: "Ubicación", admin: "Admin", reserveCTA: "Reserva tu fecha", tarifas: "Tarifas", gastronomia: "Gastronomía" },
    cta: {
      seeCalendar: "Ver disponibilidad →",
      bookDate: "Reserva tu fecha →",
      pickDate: "Elige tu fecha →",
      yourDate: "Imagínalo en Aura · elige fecha →",
      customExperience: "Crea tu experiencia 100% a medida →",
      bookOwn: "Crea tu propio evento →",
    },
    gastronomy: {
      eyebrow: "Gastronomía",
      title: "Nos adaptamos a tu paladar",
      desc: "Hacemos eventos gastronómicos de todo tipo con chef privado. Tú eliges la temática, el presupuesto y el tipo de cocina — nosotros diseñamos el menú a medida.",
    },
    tariffs: {
      eyebrow: "Tarifas",
      titleA: "Precios y tarifas de",
      titleHl: "alquiler",
      titleB: "de espacio para eventos",
      desc: "Aura está disponible todos los días de la semana. Elige la franja que mejor se adapte a tu evento.",
      capacity: "Aforo: hasta 70 personas",
      standardTitle: "Aura está disponible todos los días de la semana",
      specialsHl: "Nuestros",
      specialsTitle: "especiales",
      cta: "Reserva tu fecha →",
      footnote: "Tarifas informativas · IVA no incluido",
      colDay: "Día",
      colSchedule: "Horario",
      colShift: "Turno",
      colAmount: "Importe",
      reserveBtn: "Reservar",
      reserveBtnLong: "Reservar esta franja",
    },
    hero: {
      eyebrow: "Espacio de eventos · Sant Cugat del Vallès",
      titleA: "", titleHl: "", titleB: "",
      desc: "Aura es un espacio de eventos boutique, flexible y con personalidad propia, pensado para que marcas, empresas y particulares puedan dar forma a experiencias memorables en un entorno cuidado, contemporáneo y adaptable.",
      ctaCalendar: "Ver disponibilidad para tu evento", ctaEvents: "Entradas próximos eventos",
    },
    venue: {
      eyebrow: "Un espacio con mucha Aura",
      title: "Un espacio. Múltiples maneras de vivirlo",
      desc: "Aura responde a una nueva manera de entender los eventos. Un espacio versátil y con personalidad propia que combina diseño, calidez y flexibilidad para acoger experiencias muy diversas sin perder identidad.",
      capacityTitle: "Aforo máximo", capacityValue: "Hasta 70 personas",
      areaTitle: "Espacio", areaValue: "100 m² + terraza",
      equipTitle: "Equipamiento incluido",
      equipList: [
        "Iluminación regulable",
        "Climatización y accesibilidad",
        "Zona de cócteles y barra",
        "Terraza exterior",
        "Sonido profesional y micrófonos inalámbricos (opcionales)",
        "Proyector HD y pantalla (opcionales)",
      ],
      audienceEyebrow: "¿Para quién es Aura?",
      audienceTitle: "Un espacio que se adapta a ti",
      audiences: [
        { name: "Empresas y marcas", desc: "Lanzamientos de producto, presentaciones, cenas de equipo, jornadas y formaciones." },
        { name: "Profesionales", desc: "Showrooms, networking, workshops y talleres para clientes o comunidades." },
        { name: "Particulares", desc: "Cumpleaños, bautizos, despedidas, cenas privadas y celebraciones con chef privado." },
        { name: "Comunidades", desc: "Encuentros, fiestas temáticas y eventos culturales con personalidad." },
      ],
      dnaEyebrow: "El ADN de Aura",
      dnaTitle: "Un espacio camaleónico con Aura propia",
      dnaValues: [
        { name: "Camaleónico", desc: "Cada evento es único. La sala se transforma para acompañarlo." },
        { name: "Social", desc: "Pensado para compartir, conectar, celebrar y disfrutar en compañía." },
        { name: "Cuidado", desc: "Diseño, luz y detalles pensados para que todo encaje sin esfuerzo." },
        { name: "Atrevido", desc: "Contemporáneo, vivo y con carácter. Sin miedo a marcar la diferencia." },
      ],
    },
    events: {
      eyebrow: "La agenda de Aura",
      title: "Lo que viene, ya brilla",
      desc: "Veladas abiertas, catas, conciertos íntimos y fiestas temáticas. Elige la tuya y guárdate la plaza antes de que vuelen.",
      empty: "Próximamente publicaremos nuevos eventos. Mientras tanto, puedes reservar tu fecha privada en el calendario.",
      seeDetail: "Ver detalle →", ticket: "Entrada", publicTag: "Público", privateTag: "Privado",
      ctaBanner: "¿Quieres tu propia fecha? Reserva una tarde, noche o fin de semana — la sala es tuya.",
    },
    calendar: {
      eyebrow: "Disponibilidad", title: "Elige tu fecha",
      pitchTitle: "Convertimos cualquier día en uno especial",
      pitchDesc: "Elige una fecha libre y te la reservamos en minutos. Catas de vino, cenas de empresa, bautizos, chef privado, cumpleaños redondos o un miércoles cualquiera que quieras recordar. En Aura tú pones el motivo — nosotros, el escenario.",
      legendFree: "Libre", legendPrivate: "Privado", legendPublic: "Evento público",
      tooltipPublic: (t) => t, tooltipPrivate: "Fecha reservada",
      tooltipFree: "Fecha libre · clic para reservar",
    },
    layouts: {
      eyebrow: "Configuraciones de sala", title: "Una sala, mil experiencias",
      desc: "La misma sala cambia para cada ocasión. Algunos ejemplos han sido:",
      catering: {
        eyebrow: "Catering a medida",
        title: "Tú eliges la temática · nosotros cocinamos",
        desc: "Incluido con la sala: diseñamos un menú a medida contigo — tapas mediterráneas, cena de gala, brunch dulce o cocina internacional. Conjuntamos decoración, catering y ambiente con tu temática.",
      },
      items: [
        { name: "Celebraciones", desc: "Cumpleaños, bautizos, comuniones, puestas de largo y graduaciones. Cuidadas al detalle." },
        { name: "Eventos de empresa", desc: "Comidas y cenas de empresa, reuniones, actos corporativos y presentaciones." },
        { name: "Pop-ups", desc: "Marca invitada, lanzamientos, showrooms y activaciones efímeras con identidad." },
      ],
    },
    gallery: {
      eyebrow: "Inspiración", title: "Una nueva manera de encontrarse",
    },
    offsite: {
      eyebrow: "AURA Eventos Externos",
      title: "¿Quieres que llevemos la organización de Aura fuera de nuestro local?",
      desc: "Si tienes un espacio propio, una terraza, una masía, una oficina… nosotros llevamos toda la experiencia Aura. Te ayudamos a diseñar el evento y nos encargamos de cada pieza para que solo tengas que disfrutar.",
      items: [
        "Event management",
        "Ideas y conceptualización",
        "Diseño de experiencia",
        "Decoración",
        "Catering",
        "Barra y cócteles",
        "Animación",
        "DJ y sonido",
        "Y mucho más…",
      ],
    },
    contact: {
      eyebrow: "Hablemos", title1: "Cuéntanos cómo", titleHl: "sueñas", title2: "tu evento.",
      desc: "Responde el formulario y al enviar te abriremos WhatsApp con un mensaje listo. Coordinaremos contigo en minutos.",
      address: "Dirección", phone: "Teléfono",
      labelName: "Nombre", labelEmail: "Email", labelPhone: "Teléfono", labelMessage: "Mensaje",
      placeholderMessage: "Cuéntanos sobre tu evento ideal…",
      submit: "Enviar y abrir WhatsApp", submitting: "Enviando…",
      required: "Por favor completa los campos obligatorios.",
      sent: "Mensaje enviado. Continuamos por WhatsApp.",
    },
    map: {
      eyebrow: "Cómo llegar", title: "Rambla del Celler, 93",
      desc: "08172 Sant Cugat del Vallès, Barcelona.",
      fgcLabel: "FGC", fgcValue: "Sant Cugat — 5 min",
      parkingLabel: "Parking", parkingValue: "Plaza Lluís Millet",
      cta: "Abrir en Maps →",
    },
    footer: { nav: "Navegación", contact: "Contacto", rights: "© {year} Aura Events", adminLink: "Acceso admin" },
    reserve: {
      eyebrow: "Reserva privada",
      desc: "Déjanos tus datos y te llevamos a WhatsApp con un mensaje listo para que confirmemos contigo.",
      placeholderNotes: "Tipo de evento, número aproximado de personas…", notesLabel: "Notas (opcional)",
      submit: "Enviar y abrir WhatsApp",
      messageTemplate: (n, d, e, p, notes) => `Hola Aura, soy ${n}. Quiero reservar el ${d} para un evento privado. Email: ${e} · Tel: ${p}. ${notes ? `Notas: ${notes}` : ""}`,
      tariffEyebrow: "Reserva de franja",
      tariffTitle: "Reservar franja",
      datePrefLabel: "Fecha preferente",
      datePrefPlaceholder: "Ej: 15 de marzo de 2026",
      tariffMessage: (n, e, p, tariff, prefDate, notes) =>
        `Hola Aura, soy ${n}. Quiero reservar la franja "${tariff.name}" (${tariff.days} · ${tariff.schedule}) — ${tariff.price}€ + IVA. ${prefDate ? `Fecha preferente: ${prefDate}. ` : ""}Email: ${e} · Tel: ${p}. ${notes ? `Notas: ${notes}` : ""}`,
    },
    detail: {
      back: "Volver", tickets: "Compra de entradas", reserve: "Reserva tu plaza",
      qty: "Entradas", total: "Total", buy: "Comprar entrada", buying: "Procesando…",
      footnote: "Te redirigimos a WhatsApp para confirmar el pago.",
      type: "Tipo", typeValue: "Evento público",
      place: "Lugar", placeValue: "Aura Events, Sant Cugat", ticket: "Entrada",
    },
  },
  en: {
    nav: { sala: "The Venue", eventos: "Events", disponibilidad: "Availability", configuraciones: "Experiences", galeria: "Gallery", contacto: "Contact", ubicacion: "Location", admin: "Admin", reserveCTA: "Book your date", tarifas: "Pricing", gastronomia: "Gastronomy" },
    cta: {
      seeCalendar: "See availability →",
      bookDate: "Book your date →",
      pickDate: "Pick your date →",
      yourDate: "Picture it at Aura · pick a date →",
      customExperience: "Build your fully custom experience →",
      bookOwn: "Plan your own event →",
    },
    gastronomy: {
      eyebrow: "Gastronomy",
      title: "We adapt to your palate",
      desc: "We host all kinds of gastronomic events with private chef. You pick the theme, the budget and the cuisine — we design the menu to measure.",
    },
    tariffs: {
      eyebrow: "Pricing",
      titleA: "Space rental",
      titleHl: "pricing",
      titleB: "for events in Sant Cugat",
      desc: "Aura is available every day of the week. Pick the slot that best fits your event.",
      capacity: "Capacity: up to 70 guests",
      standardTitle: "Aura is open every day of the week",
      specialsHl: "Our",
      specialsTitle: "specials",
      cta: "Book your date →",
      footnote: "Indicative prices · VAT not included",
      colDay: "Day",
      colSchedule: "Schedule",
      colShift: "Shift",
      colAmount: "Amount",
      reserveBtn: "Book",
      reserveBtnLong: "Book this slot",
    },
    hero: {
      eyebrow: "Event venue · Sant Cugat del Vallès",
      titleA: "", titleHl: "", titleB: "",
      desc: "Aura is a boutique event space, flexible and with its own personality, designed for brands, companies and individuals to shape memorable experiences in a refined, contemporary and adaptable setting.",
      ctaCalendar: "See availability for your event", ctaEvents: "Tickets for upcoming events",
    },
    venue: {
      eyebrow: "A space with lots of Aura",
      title: "One space. Many ways to live it",
      desc: "Aura answers a new way of understanding events. A versatile space with its own personality that blends design, warmth and flexibility to host very diverse experiences without losing identity.",
      capacityTitle: "Max capacity", capacityValue: "Up to 70 guests",
      areaTitle: "Space", areaValue: "100 m² + terrace",
      equipTitle: "Included equipment",
      equipList: [
        "Dimmable lighting",
        "Climate control & accessibility",
        "Cocktail bar area",
        "Outdoor terrace",
        "Pro sound system & wireless microphones (optional)",
        "HD projector & screen (optional)",
      ],
      audienceEyebrow: "Who is Aura for?",
      audienceTitle: "A space that adapts to you",
      audiences: [
        { name: "Companies & brands", desc: "Product launches, presentations, team dinners, summits and training sessions." },
        { name: "Professionals", desc: "Showrooms, networking, workshops and masterclasses for clients or communities." },
        { name: "Individuals", desc: "Birthdays, christenings, farewells, private dinners and private-chef celebrations." },
        { name: "Communities", desc: "Gatherings, themed parties and cultural events with personality." },
      ],
      dnaEyebrow: "Aura's DNA",
      dnaTitle: "A chameleon space with its own Aura",
      dnaValues: [
        { name: "Chameleon", desc: "Every event is unique. The room transforms to match it." },
        { name: "Social", desc: "Made for sharing, connecting, celebrating and enjoying together." },
        { name: "Crafted", desc: "Design, light and details so everything just fits effortlessly." },
        { name: "Daring", desc: "Contemporary, alive and bold. Not afraid to stand out." },
      ],
    },
    events: {
      eyebrow: "Aura's agenda", title: "Our own events",
      desc: "Open evenings, tastings, intimate concerts and themed parties. Pick yours and save your seat before they fly.",
      empty: "We'll publish new events soon. Meanwhile, you can book your private date in the calendar.",
      seeDetail: "See detail →", ticket: "Ticket", publicTag: "Public", privateTag: "Private",
      ctaBanner: "Want your own date? Book an afternoon, evening or weekend — the room is yours.",
    },
    calendar: {
      eyebrow: "Availability", title: "Pick your date",
      pitchTitle: "We turn any day into a special one",
      pitchDesc: "Pick a free date and we'll hold it for you in minutes. Wine tastings, company dinners, christenings, private chef nights, milestone birthdays or just a random Wednesday you want to remember. At Aura you bring the reason — we bring the stage.",
      legendFree: "Free", legendPrivate: "Private", legendPublic: "Public event",
      tooltipPublic: (t) => t, tooltipPrivate: "Date booked",
      tooltipFree: "Free date · click to book",
    },
    layouts: {
      eyebrow: "Room layouts", title: "One room, a thousand experiences",
      desc: "The same room changes for every occasion. Some examples have been:",
      catering: {
        eyebrow: "Custom catering",
        title: "You pick the theme · we cook",
        desc: "Included with the venue: we design a custom menu with you — Mediterranean tapas, gala dinner, sweet brunch or international cuisine. We match decor, catering and ambience to your theme.",
      },
      items: [
        { name: "Celebrations", desc: "Birthdays, christenings, communions, debutantes and graduations. Crafted to the detail." },
        { name: "Corporate events", desc: "Company lunches and dinners, meetings, corporate acts and presentations." },
        { name: "Pop-ups", desc: "Guest brands, launches, showrooms and ephemeral activations with identity." },
      ],
    },
    gallery: {
      eyebrow: "Inspiration", title: "A new way to meet",
    },
    offsite: {
      eyebrow: "AURA External Events",
      title: "Want us to bring the Aura experience to your own venue?",
      desc: "If you have your own space, a terrace, a country house, an office… we bring the full Aura experience to you. We help you design the event and take care of every piece so you only have to enjoy it.",
      items: [
        "Event management",
        "Ideas & concept",
        "Experience design",
        "Decoration",
        "Catering",
        "Bar & cocktails",
        "Entertainment",
        "DJ & sound",
        "And much more…",
      ],
    },
    contact: {
      eyebrow: "Let's talk", title1: "Tell us how you", titleHl: "dream", title2: "your event.",
      desc: "Fill the form and on submit we'll open WhatsApp with a message ready. We'll coordinate with you in minutes.",
      address: "Address", phone: "Phone",
      labelName: "Name", labelEmail: "Email", labelPhone: "Phone", labelMessage: "Message",
      placeholderMessage: "Tell us about your ideal event…",
      submit: "Send & open WhatsApp", submitting: "Sending…",
      required: "Please complete the required fields.",
      sent: "Message sent. We continue on WhatsApp.",
    },
    map: {
      eyebrow: "How to get here", title: "Rambla del Celler, 93",
      desc: "08172 Sant Cugat del Vallès, Barcelona.",
      fgcLabel: "FGC", fgcValue: "Sant Cugat — 5 min",
      parkingLabel: "Parking", parkingValue: "Lluís Millet Square",
      cta: "Open in Maps →",
    },
    footer: { nav: "Navigation", contact: "Contact", rights: "© {year} Aura Events", adminLink: "Admin access" },
    reserve: {
      eyebrow: "Private booking",
      desc: "Leave your details and we'll take you to WhatsApp with a ready-to-send message so we can confirm with you.",
      placeholderNotes: "Type of event, approximate number of guests…", notesLabel: "Notes (optional)",
      submit: "Send & open WhatsApp",
      messageTemplate: (n, d, e, p, notes) => `Hi Aura, I'm ${n}. I'd like to book ${d} for a private event. Email: ${e} · Phone: ${p}. ${notes ? `Notes: ${notes}` : ""}`,
      tariffEyebrow: "Slot booking",
      tariffTitle: "Book slot",
      datePrefLabel: "Preferred date",
      datePrefPlaceholder: "e.g. March 15, 2026",
      tariffMessage: (n, e, p, tariff, prefDate, notes) =>
        `Hi Aura, I'm ${n}. I'd like to book the "${tariff.name}" slot (${tariff.days} · ${tariff.schedule}) — ${tariff.price}€ + VAT. ${prefDate ? `Preferred date: ${prefDate}. ` : ""}Email: ${e} · Phone: ${p}. ${notes ? `Notes: ${notes}` : ""}`,
    },
    detail: {
      back: "Back", tickets: "Ticket purchase", reserve: "Book your seat",
      qty: "Tickets", total: "Total", buy: "Buy ticket", buying: "Processing…",
      footnote: "We'll redirect you to WhatsApp to confirm the payment.",
      type: "Type", typeValue: "Public event",
      place: "Venue", placeValue: "Aura Events, Sant Cugat", ticket: "Ticket",
    },
  },
};

const LOCALE_LABELS = { ca: "CA", es: "ES", en: "EN" };

const I18nContext = createContext({ locale: "ca", setLocale: () => {}, t: DICT.ca, locales: LOCALE_LABELS });

export function I18nProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    try {
      // 1. URL param (?lang=) has highest priority for hreflang/SEO
      const urlLang = new URLSearchParams(window.location.search).get("lang");
      if (urlLang && DICT[urlLang]) return urlLang;
      // 2. User's explicit choice saved in localStorage
      const saved = localStorage.getItem("aura.locale");
      if (saved && DICT[saved]) return saved;
      // 3. Default → Catalan (do NOT auto-detect browser lang)
      return "ca";
    } catch {
      return "ca";
    }
  });
  useEffect(() => {
    try { localStorage.setItem("aura.locale", locale); } catch (e) { /* localStorage unavailable */ console.debug(e); }
    document.documentElement.lang = locale;
  }, [locale]);
  const value = { locale, setLocale, t: DICT[locale], locales: LOCALE_LABELS };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);

export const formatDateLocale = (iso, locale = "ca") => {
  if (!iso) return "";
  const lc = { ca: "ca-ES", es: "es-ES", en: "en-GB" }[locale] || "ca-ES";
  return new Date(`${iso}T00:00:00`).toLocaleDateString(lc, {
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });
};

export const formatDateShort = (iso, locale = "ca") => {
  if (!iso) return "";
  const lc = { ca: "ca-ES", es: "es-ES", en: "en-GB" }[locale] || "ca-ES";
  return new Date(`${iso}T00:00:00`).toLocaleDateString(lc, {
    day: "2-digit", month: "long", year: "numeric",
  });
};

export const MONTH_NAMES = {
  ca: ["Gener","Febrer","Març","Abril","Maig","Juny","Juliol","Agost","Setembre","Octubre","Novembre","Desembre"],
  es: ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"],
  en: ["January","February","March","April","May","June","July","August","September","October","November","December"],
};

export const DOW_NAMES = {
  ca: ["Dl","Dt","Dc","Dj","Dv","Ds","Dg"],
  es: ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"],
  en: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"],
};
